import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Plus, Copy, Trash2, Power, Eye, RefreshCw, Download, RotateCcw, AlertTriangle } from 'lucide-react';
import { adminApi } from '../utils/api';
import { toast } from 'sonner';
import { UserDetailsModal } from '../components/UserDetailsModal';
import { UserFilterSidebar } from '../components/UserFilterSidebar';
import { BulkActionToolbar } from '../components/BulkActionToolbar';

export const Users = ({ adminSecret }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    status: null,
    search: '',
    sortBy: 'created_at',
    order: 'desc'
  });
  
  // Modals
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Forms
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  
  // Bulk operations
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState('');
  
  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, filters, adminSecret]);
  
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers(
        adminSecret,
        pagination.page,
        pagination.limit,
        filters.status,
        filters.search,
        filters.sortBy,
        filters.order
      );
      setUsers(response.data.users);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages
      }));
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newUser.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      const response = await adminApi.createUser(adminSecret, newUser);
      toast.success('User created successfully');
      toast.info(`API Key: ${response.data.api_key}`, { duration: 10000 });
      setIsCreateOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };
  
  const handleViewDetails = async (apiKey) => {
    setIsDetailsOpen(true);
    setLoadingDetails(true);
    try {
      const response = await adminApi.getUserDetails(adminSecret, apiKey);
      setSelectedUserDetails(response.data);
    } catch (error) {
      toast.error('Failed to load user details');
      setIsDetailsOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const handleToggleUser = async (apiKey, currentStatus) => {
    try {
      await adminApi.toggleUser(adminSecret, apiKey);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to toggle user status');
    }
  };
  
  const handleRegenerateKey = async (apiKey, userName) => {
    if (!window.confirm(
      `⚠️ SECURITY WARNING\n\nRegenerating the API key for "${userName}" will invalidate the old key immediately.\n\nThis action cannot be undone. Continue?`
    )) return;
    
    try {
      const response = await adminApi.regenerateApiKey(adminSecret, apiKey);
      toast.success('API key regenerated successfully');
      toast.info(`New API Key: ${response.data.new_key}`, { duration: 15000 });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to regenerate API key');
    }
  };
  
  const handleDeleteUser = async (apiKey, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) return;
    
    try {
      await adminApi.deleteUser(adminSecret, apiKey);
      toast.success('User deleted successfully');
      fetchUsers();
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(apiKey);
        return newSet;
      });
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };
  
  const handleBulkActivate = async () => {
    if (selectedUsers.size === 0) return;
    
    try {
      await adminApi.bulkUpdateUsers(adminSecret, Array.from(selectedUsers), 'activate');
      toast.success(`Activated ${selectedUsers.size} users`);
      setSelectedUsers(new Set());
      fetchUsers();
    } catch (error) {
      toast.error('Failed to bulk activate users');
    }
  };
  
  const handleBulkDeactivate = async () => {
    if (selectedUsers.size === 0) return;
    
    try {
      await adminApi.bulkUpdateUsers(adminSecret, Array.from(selectedUsers), 'deactivate');
      toast.success(`Deactivated ${selectedUsers.size} users`);
      setSelectedUsers(new Set());
      fetchUsers();
    } catch (error) {
      toast.error('Failed to bulk deactivate users');
    }
  };
  
  const handleBulkDeleteConfirm = async () => {
    if (selectedUsers.size === 0) return;
    
    // Security: Require confirmation
    if (bulkDeleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    try {
      // Generate secure confirmation token
      const confirmationToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
      await adminApi.bulkDeleteUsers(adminSecret, Array.from(selectedUsers), confirmationToken);
      toast.success(`Deleted ${selectedUsers.size} users`);
      setSelectedUsers(new Set());
      setIsBulkDeleteDialogOpen(false);
      setBulkDeleteConfirmation('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk delete users');
    }
  };
  
  const handleExport = async (format) => {
    try {
      const response = await adminApi.exportUsers(adminSecret, format, filters.status);
      
      if (format === 'csv') {
        // Download CSV
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Users exported successfully');
      } else {
        // JSON format
        const dataStr = JSON.stringify(response.data.users, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Users exported successfully');
      }
    } catch (error) {
      toast.error('Failed to export users');
    }
  };
  
  const copyApiKey = (apiKey) => {
    navigator.clipboard.writeText(apiKey);
    toast.success('API Key copied to clipboard');
  };
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1
  };
  
  const handleResetFilters = () => {
    setFilters({
      status: null,
      search: '',
      sortBy: 'created_at',
      order: 'desc'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  const toggleUserSelection = (apiKey) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(apiKey)) {
        newSet.delete(apiKey);
      } else {
        newSet.add(apiKey);
      }
      return newSet;
    });
  };
  
  const selectAllUsers = () => {
    setSelectedUsers(new Set(users.map(u => u.api_key)));
  };
  
  const deselectAllUsers = () => {
    setSelectedUsers(new Set());
  };
  
  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-indicator">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-2">Manage API users and their access</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Export Button */}
          <Select onValueChange={handleExport}>
            <SelectTrigger className="w-[140px]" data-testid="export-button">
              <Download className="h-4 w-4 mr-2" />
              Export
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">Export as CSV</SelectItem>
              <SelectItem value="json">Export as JSON</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Create User Button */}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-button">
                <Plus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-user-dialog">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Company Name"
                    data-testid="user-name-input"
                    maxLength={100}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="user@example.com"
                    data-testid="user-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password (min 8 characters)</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUser.password || ''}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Secure password"
                    data-testid="user-password-input"
                    minLength={8}
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                  >
                    <SelectTrigger id="role" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="developer">Developer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-user-button">
                  Create User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Filters - Compact 2 rows */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <Label className="text-xs text-gray-600 mb-1">Search</Label>
              <Input
                placeholder="Name or email..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                data-testid="search-input"
              />
            </div>
            
            {/* Status */}
            <div>
              <Label className="text-xs text-gray-600 mb-1">Status</Label>
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => handleFilterChange('status', value === 'all' ? null : value)}
              >
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Sort By */}
            <div>
              <Label className="text-xs text-gray-600 mb-1">Sort By</Label>
              <Select
                value={filters.sortBy || 'created_at'}
                onValueChange={(value) => handleFilterChange('sortBy', value)}
              >
                <SelectTrigger data-testid="sort-by-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="created_at">Date</SelectItem>
                  <SelectItem value="requests_count">Requests</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Order */}
            <div>
              <Label className="text-xs text-gray-600 mb-1">Order</Label>
              <Select
                value={filters.order || 'desc'}
                onValueChange={(value) => handleFilterChange('order', value)}
              >
                <SelectTrigger data-testid="sort-order-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetFilters}
              data-testid="reset-filters-button"
            >
              Reset Filters
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Main Content */}
      <div className="space-y-4">
        {/* Bulk Actions */}
          {selectedUsers.size > 0 && (
            <BulkActionToolbar
              selectedCount={selectedUsers.size}
              totalCount={users.length}
              onSelectAll={selectAllUsers}
              onDeselectAll={deselectAllUsers}
              onBulkActivate={handleBulkActivate}
              onBulkDeactivate={handleBulkDeactivate}
              onBulkDelete={() => setIsBulkDeleteDialogOpen(true)}
            />
          )}
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Users ({pagination.total})</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchUsers}
                  data-testid="refresh-button"
                  disabled={loading}
                >
                  <RotateCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedUsers.size === users.length && users.length > 0}
                          onCheckedChange={(checked) => checked ? selectAllUsers() : deselectAllUsers()}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>API Key</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Auth Method</TableHead>
                      <TableHead>Requests</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.api_key} data-testid={`user-row-${user.email}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUsers.has(user.api_key)}
                            onCheckedChange={() => toggleUserSelection(user.api_key)}
                            data-testid={`select-user-${user.email}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge
                            variant={user.role === 'admin' ? 'default' : 'secondary'}
                            data-testid={`role-${user.email}`}
                          >
                            {user.role?.toUpperCase() || 'USER'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {user.api_key.substring(0, 20)}...
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyApiKey(user.api_key)}
                              data-testid={`copy-key-${user.email}`}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={user.is_active ? 'success' : 'destructive'}
                            className={user.is_active ? 'bg-green-500 text-white' : ''}
                            data-testid={`status-${user.email}`}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.metadata?.oauth_provider ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                <svg className="mr-1 h-3 w-3" viewBox="0 0 488 512" fill="currentColor">
                                  <path d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
                                </svg>
                                Google
                              </Badge>
                              {!user.is_active && (
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                                  Pending
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-gray-600">Password</Badge>
                          )}
                        </TableCell>
                        <TableCell>{user.requests_count || 0}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleViewDetails(user.api_key)}
                              title="View Details"
                              data-testid={`view-details-${user.email}`}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleToggleUser(user.api_key, user.is_active)}
                              title={user.is_active ? 'Deactivate' : 'Activate'}
                              data-testid={`toggle-user-${user.email}`}
                            >
                              <Power className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleRegenerateKey(user.api_key, user.name)}
                              title="Regenerate API Key"
                              data-testid={`regenerate-key-${user.email}`}
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteUser(user.api_key, user.name)}
                              title="Delete User"
                              data-testid={`delete-user-${user.email}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.pages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      data-testid="prev-page-button"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.pages}
                      data-testid="next-page-button"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      
      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedUserDetails(null);
        }}
        userDetails={selectedUserDetails}
        loading={loadingDetails}
      />
      
      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-6 w-6" />
              Confirm Bulk Delete
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              You are about to permanently delete <strong>{selectedUsers.size}</strong> users.
              This action cannot be undone.
            </p>
            <div>
              <Label htmlFor="confirm-delete">Type <strong>DELETE</strong> to confirm:</Label>
              <Input
                id="confirm-delete"
                value={bulkDeleteConfirmation}
                onChange={(e) => setBulkDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
                data-testid="bulk-delete-confirmation-input"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsBulkDeleteDialogOpen(false);
                setBulkDeleteConfirmation('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteConfirm}
              disabled={bulkDeleteConfirmation !== 'DELETE'}
              data-testid="confirm-bulk-delete-button"
            >
              Delete {selectedUsers.size} Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};